import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const BlogSection = () => {
  const featuredArticles = [
    {
      id: "cnc-lathe-optimization",
      title: "CNC Lathe Drive Alarm Troubleshooting",
      titleCN: "CNC车床驱动报警故障排查",
      description: "A practical repair note on checking drive alarms, machine condition, and common causes before returning a CNC lathe to production.",
      descriptionCN: "记录CNC车床驱动报警的排查流程，包括机床状态检查、常见原因判断和恢复生产前的确认。",
      category: "Repair Note",
      categoryCN: "维修记录",
      date: "CNC Lathe",
      readTime: "Service example",
      machine: "CNC Lathe",
      machineCN: "CNC车床",
      image: "/images/case-studies/cnc-lathe.jpg"
    },
    {
      id: "5-axis-mill-enhancement",
      title: "5-Axis CNC Mill Preventive Maintenance",
      titleCN: "五轴CNC铣床预防性保养",
      description: "Routine inspection and maintenance work for a 5-axis CNC mill, covering alignment checks, lubrication, and machine condition review.",
      descriptionCN: "五轴CNC铣床的日常检查与保养，包括精度检查、润滑系统和机床状态确认。",
      category: "Maintenance Note",
      categoryCN: "保养记录",
      date: "5-Axis Mill",
      readTime: "Service example",
      machine: "5-Axis CNC Mill",
      machineCN: "五轴CNC铣床",
      image: "/images/case-studies/5-axis-mill.jpg"
    },
    {
      id: "edm-precision",
      title: "EDM Machine Calibration & Service",
      titleCN: "电火花机校准与保养",
      description: "Servicing an EDM machine through inspection, calibration checks, and adjustment work to keep machining results consistent.",
      descriptionCN: "通过检查、校准与调整，协助电火花机保持稳定的加工状态。",
      category: "Service Note",
      categoryCN: "服务记录",
      date: "EDM Machine",
      readTime: "Service example",
      machine: "EDM Machine",
      machineCN: "电火花加工机",
      image: "/images/case-studies/edm-machine.jpg"
    },
    {
      id: "wire-cut-optimization",
      title: "Wire Cut EDM Alignment Check",
      titleCN: "线切割电火花机校正检查",
      description: "A wire cut EDM service visit focused on alignment, consumable checks, and cutting stability.",
      descriptionCN: "线切割电火花机的维修检查，重点包括校正、耗材检查和切割稳定性确认。",
      category: "Repair Note",
      categoryCN: "维修记录",
      date: "Wire Cut EDM",
      readTime: "Service example",
      machine: "Wire Cut EDM",
      machineCN: "线切割电火花机",
      image: "/images/case-studies/wire-cut.jpg"
    },
    {
      id: "grinding-machine",
      title: "Surface Grinding Machine Retrofit",
      titleCN: "平面磨床改造升级",
      description: "Retrofitting and adjustment work for an older surface grinder to improve reliability and day-to-day usability.",
      descriptionCN: "为老旧平面磨床进行改造与调整，提升日常使用的稳定性与操作便利性。",
      category: "Retrofit Note",
      categoryCN: "改造记录",
      date: "Surface Grinder",
      readTime: "Service example",
      machine: "Surface Grinder",
      machineCN: "平面磨床",
      image: "/images/case-studies/surface-grinder.jpg"
    },
    {
      id: "machining-center",
      title: "Vertical Machining Center Service Visit",
      titleCN: "立式加工中心维修服务",
      description: "Troubleshooting and maintenance for a vertical machining center, including machine checks and practical recommendations for the operator.",
      descriptionCN: "立式加工中心的故障排查与保养，包括机床检查和给操作人员的实用建议。",
      category: "Service Note",
      categoryCN: "服务记录",
      date: "VMC",
      readTime: "Service example",
      machine: "VMC",
      machineCN: "立式加工中心",
      image: "/images/case-studies/vmc.jpg"
    }
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-2 text-wiiseng-navy">
            Recent CNC Repair Work
          </h2>
          <p
            className="text-xl md:text-2xl font-semibold mb-4 text-wiiseng-navy/80"
            lang="zh"
          >
            近期CNC维修项目
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Practical examples of the CNC machine service, repair, and maintenance work we handle for workshops and manufacturers around Shah Alam and the Klang Valley.
          </p>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" lang="zh">
            展示我们为莎阿南及巴生谷一带车间和制造商处理的CNC机床维修、保养与服务项目。
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {featuredArticles.map((article, index) => (
            <Card key={index} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-wiiseng-blue">{article.category}</span>
                  <span className="text-sm text-gray-500">•</span>
                  <span className="text-sm text-gray-500">{article.date}</span>
                </div>
                <CardTitle className="text-xl">{article.title}</CardTitle>
                <CardTitle className="text-xl text-gray-600">{article.titleCN}</CardTitle>
                <div className="mt-2">
                  <span className="text-sm font-medium text-wiiseng-blue">{article.machine}</span>
                  <span className="text-sm text-gray-500"> / </span>
                  <span className="text-sm text-gray-500">{article.machineCN}</span>
                </div>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600 mb-2">
                  {article.description}
                </CardDescription>
                <CardDescription className="text-gray-600 mb-4">
                  {article.descriptionCN}
                </CardDescription>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">{article.readTime}</span>
                  <Link to={`/case-studies/${article.id}`}>
                    <Button variant="ghost" className="text-wiiseng-blue hover:text-wiiseng-navy">
                      Read More <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/case-studies" aria-label="View all CNC repair notes">
            <Button variant="outline" className="text-wiiseng-navy border-wiiseng-navy hover:bg-wiiseng-navy hover:text-white">
              View More Repair Notes
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection; 